import React from 'react';
import PropTypes from 'prop-types';
import { TextField as MUITextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { colors } from '@constants/colors';
import { fonts } from '@constants/fonts';

const getInputLabel = ({ floatingLabelFixed = false }) => {
  return floatingLabelFixed
    ? { position: 'unset', color: colors.neutral.gray }
    : {
      color: colors.neutral.gray,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      bottom: '0px',
      maxWidth: '100%'
    };
};

const getInputLabelShrink = ({ floatingLabelFixed = false }) => {
  return floatingLabelFixed
    ? { transform: 'unset', fontSize: fonts.sm }
    : {
      fontSize: fonts.sm,
      overflow: 'unset',
      textOverflow: 'unset',
      whiteSpace: 'unset'
    };
};

const useStyles = makeStyles(theme => ({
  inputRoot: {
    fontSize: fonts.md,
    '&.Mui-disabled': {
      color: colors.neutral.gray,
      WebkitTextFillColor: colors.neutral.gray
    }
  },
  inputUnderline: {
    '&:before': {
      borderBottom: `1px solid ${colors.neutral.silver}`
    }
  },
  inputLabel: getInputLabel,
  inputLabelShrunk: getInputLabelShrink,
  inputLabelFocused: {
    color: `${colors.neutral.gray} !important`
  },
  inputLabelError: {
    color: `${theme.palette.error.main} !important`
  },
  formControl: {
    'label + &': {
      marginTop: '0'
    }
  }
}));

export const TextField = React.forwardRef((props, ref) => {
  const {
    floatingLabelFixed = false,
    fullWidth = true,
    autoComplete = 'off',
    disableUnderline = false,
    variant = 'standard',
    ...data
  } = props;

  const classes = useStyles({ floatingLabelFixed });

  return (
    <MUITextField
      {...data}
      ref={ref}
      variant={variant}
      fullWidth={fullWidth}
      label={props.label}
      autoComplete={autoComplete}
      InputLabelProps={{
        ...props.InputLabelProps,
        ...(floatingLabelFixed && {
          classes: {
            root: classes.inputLabel,
            focused: classes.inputLabelFocused,
            error: classes.inputLabelError,
            shrink: classes.inputLabelShrunk,
            ...props.InputLabelProps?.classes
          },
          shrink: true
        })
      }}
      InputProps={{
        ...props.InputProps,
        ...(variant === 'standard' && { disableUnderline }),
        classes: {
          root: classes.inputRoot,
          ...(variant === 'standard' && {
            underline: classes.inputUnderline,
            ...(floatingLabelFixed && { formControl: classes.formControl })
          }),
          ...props.InputProps?.classes
        }
      }}
    />
  );
});

TextField.propTypes = {
  FormHelperTextProps: PropTypes.object,
  InputLabelProps: PropTypes.object,
  InputProps: PropTypes.object,
  autoComplete: PropTypes.string,
  disableUnderline: PropTypes.bool,
  floatingLabelFixed: PropTypes.bool,
  fullWidth: PropTypes.bool,
  label: PropTypes.string,
  style: PropTypes.object,
  variant: PropTypes.string
};

export default TextField;
